<script>
import { validationMixin } from 'vuelidate'
import { required, maxLength, between, decimal } from 'vuelidate/lib/validators'
import { rules } from '@/utils/validators'

import UploadImage from '@/components/UploadImage'

import { GET_PRODUCT_QUERY } from '@/graph/gql/product.gql'
import { createProduct, updateProduct } from '@/graph/mutations/product'

import allergens from '@/assets/allergens-options.js'
import diets from '@/assets/diets-options.js'

const NEW_ITEM_ROUTE = 'new'
const MIN_PRICE = 0.01
const MAX_PRICE = 100

export default {
  name: 'MenuItemView',
  components: { UploadImage },
  mixins: [validationMixin],
  props: {
    id: { type: String, default: () => NEW_ITEM_ROUTE }
  },
  apollo: {
    item: {
      query: GET_PRODUCT_QUERY,
      fetchPolicy: 'cache-and-network',
      variables() {
        return { id: this.id }
      },
      update: data => data.product,
      result({ data }) {
        if (!data) return
        // Prefill form
        const {
          photo,
          name,
          description,
          price,
          allergens,
          diets
        } = data.product
        this.form = { photo, name, description, price, allergens, diets }
      },
      skip() {
        return !this.isEditForm
      }
    }
  },
  data() {
    return {
      rules: {
        ...rules,
        minPrice: x => x >= MIN_PRICE || `Minimum price is RM ${MIN_PRICE}`,
        maxPrice: x => x <= MAX_PRICE || `Maximum price is RM ${MAX_PRICE}`
      },
      allergens,
      diets,
      form: {
        photo: null,
        name: '',
        description: '',
        price: null,
        allergens: [],
        diets: []
      },

      uploadedFile: undefined,
      submitting: false
    }
  },
  computed: {
    isAddForm() {
      return this.id === NEW_ITEM_ROUTE
    },

    isEditForm() {
      return this.id !== NEW_ITEM_ROUTE
    }
  },
  methods: {
    onFileChange({ src, file }) {
      this.uploadedFile = file
      this.form.photo = src
    },

    addProduct(data) {
      // TODO: add error handling
      return createProduct(data).then(() => {
        this.$toast('Menu item added')
      })
    },

    editProduct(id, data) {
      // TODO: add error handling
      return updateProduct(id, data).then(() => {
        this.$toast('Menu item updated')
      })
    },

    submitForm() {
      if (this.submitting || this.$v.$invalid) return
      this.submitting = true

      const { name, description, price, allergens, diets } = this.form
      const data = {
        name,
        description,
        price,
        allergens,
        diets,
        photo: this.uploadedFile
      }

      const submit = this.isEditForm
        ? this.editProduct(this.id, data)
        : this.addProduct(data)

      return submit
        .then(() => this.$router.push({ name: 'MenuView' }))
        .finally(() => (this.submitting = false))
    }
  },
  validations: {
    form: {
      photo: { required },
      name: { required, maxLength: maxLength(25) },
      description: { required, maxLength: maxLength(140) },
      price: { required, decimal, between: between(MIN_PRICE, MAX_PRICE) }
    }
  }
}
</script>

<template>
  <transition name="slide-up" appear>
    <!-- New Menu item view page -->
    <div class="view-grid">
      <div>
        <div class="display-1 font-weight-bold mb-7">
          {{ isAddForm ? 'New' : 'Edit' }} menu item
        </div>

        <div>
          <v-skeleton-loader
            :loading="this.$apollo.loading"
            transition="fade-transition"
            height="94"
            type="list-item-avatar-two-line"
          >
            <!-- Product form -->
            <form @submit.prevent="submitForm()">
              <div class="d-flex align-center mt-3">
                <UploadImage
                  :default-src="form.photo"
                  :size="100"
                  @input="onFileChange"
                />

                <span class="caption secondary--text ml-6">
                  High quality photos can <br />
                  more than triple your sales
                </span>
              </div>

              <v-text-field
                v-model.trim="form.name"
                :rules="[rules.required]"
                validate-on-blur
                label="Name"
                color="secondary"
                class="mt-6"
                counter
                maxlength="25"
              />

              <v-textarea
                v-model="form.description"
                :rules="[rules.required]"
                validate-on-blur
                name="story"
                label="Description"
                counter
                maxlength="140"
                rows="1"
                auto-grow
                class="my-2"
              />

              <v-text-field
                v-model.number="form.price"
                :rules="[rules.required, rules.minPrice, rules.maxPrice]"
                validate-on-blur
                label="Sales price"
                prefix="RM"
                class="my-2 secondary--text font-weight-regular"
              />

              <v-select
                v-show="false"
                v-model="form.allergens"
                :items="allergens"
                item-text="name"
                item-value="value"
                attach
                chips
                deletable-chips
                small-chips
                label="Allergens"
                multiple
                class="menu-item--chips-select"
              />

              <v-select
                v-model="form.diets"
                :items="diets"
                item-text="name"
                item-value="value"
                attach
                chips
                deletable-chips
                small-chips
                label="Diets"
                multiple
                class="menu-item--chips-select"
              />

              <v-btn
                :loading="submitting"
                :disabled="$v.$invalid"
                type="submit"
                depressed
                rounded
                color="primary"
                class="my-6 px-6"
              >
                {{ isAddForm ? 'Add item' : 'Save changes' }}
              </v-btn>
            </form>
          </v-skeleton-loader>
        </div>
      </div>
    </div>
  </transition>
</template>

<style lang="sass">
.menu-item--chips-select .v-chip
  border: none !important
  margin-right: 0 !important
</style>
